import React, { useState } from 'react';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { setSignupData } from '../../redux/slices/authSlice';
import { ACCOUNT_TYPE } from '../../utils/constants';
import { useDispatch } from 'react-redux';
import { sendOtp } from '../../services/operations/authApi';
import { useForm } from 'react-hook-form';
import PasswordValidator from '../common/PasswordValidator';
import SwitchButton from '../common/SwitchButton';

const SignUpForm = ({ setConfirmationModal }) => {
  const [accountType, setAccountType] = useState(ACCOUNT_TYPE.STUDENT);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitSuccessful }
  } = useForm();

  const signUpSubmitHandler = (data) => {
    if (data.password !== data.confirmPassword) {
      setConfirmationModal({
        text1: 'Password Mismatch ❗️',
        text2: 'The password and confirm password entries do not match. Please ensure they are identical and try again.',
        closeModal: () => setConfirmationModal(null),
      })
      return;
    }
    const signupData = { ...data, accountType };
    dispatch(setSignupData(signupData));
    const email = data.email.toLowerCase();
    dispatch(sendOtp(data.firstName, data.lastName, email, navigate, setConfirmationModal));
    if (isSubmitSuccessful) {
      reset({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
      });
      setAccountType(ACCOUNT_TYPE.STUDENT);
    }

  };
  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  return (
    <div>
      <div>
        <div>
          <SwitchButton
            firstHandler={() => (setAccountType(ACCOUNT_TYPE.STUDENT))}
            firstName='Student'
            secondHandler={() => (setAccountType(ACCOUNT_TYPE.INSTRUCTOR))}
            secondName='Instructor'
            selectedType={accountType}
            type1='Student'
            type2='Instructor'
          />
        </div>
        <form className='w-full flex flex-col md:gap-y-4 gap-y-6' onSubmit={handleSubmit(signUpSubmitHandler)}>
          <div className='flex md:flex-row flex-col gap-x-4 md:gap-y-0 gap-y-6'>
            <label>
              <p className='text-[0.875rem] leading-[1.375rem] text-richblack-5 mb-1'>First Name <sup className='text-pink-200'>*</sup></p>
              <input
                className='w-full p-[12px] bg-richblack-800 rounded-[0.5rem] text-richblack-5 outline-none'
                style={{ boxShadow: 'rgba(255, 255, 255, 0.18) 0px -1px 0px inset' }}
                type='text'
                name='firstName'
                placeholder='First Name'
                {...register('firstName', { required: true })}
              />
              {errors.firstName && (
                <span className='text-pink-200 text-sm'>Please enter your name</span>
              )}
            </label>
            <label>
              <p className='text-[0.875rem] leading-[1.375rem] text-richblack-5 mb-1'>Last Name <sup className='text-pink-200'>*</sup></p>
              <input
                className='w-full p-[12px] bg-richblack-800 rounded-[0.5rem] text-richblack-5 outline-none'
                style={{ boxShadow: 'rgba(255, 255, 255, 0.18) 0px -1px 0px inset' }}
                type='text'
                name='lastName'
                placeholder='Last Name'
                {...register('lastName')}
              />
            </label>
          </div>
          <label className='w-full'>
            <p className='text-[0.875rem] leading-[1.375rem] text-richblack-5 mb-1'>Email Address <sup className='text-pink-200'>*</sup></p>
            <input
              className='p-[12px] bg-richblack-800 rounded-[0.5rem] text-richblack-5 w-full outline-none'
              style={{ boxShadow: 'rgba(255, 255, 255, 0.18) 0px -1px 0px inset' }}
              type='email'
              name='email'
              placeholder='Enter email address'
              {...register('email', { required: true })}
            />
            {errors.email && (
              <span className='text-pink-200 text-sm'>Please enter your email</span>
            )}
          </label>
          <div className='flex md:flex-row flex-col gap-x-4 md:gap-y-0 gap-y-6'>
            <label className='relative'>
              <p className='text-[0.875rem] leading-[1.375rem] text-richblack-5 mb-1'>Create Password <sup className='text-pink-200'>*</sup></p>
              <input
                className='w-full p-[12px] pr-10 bg-richblack-800 rounded-[0.5rem] text-richblack-5 outline-none'
                style={{ boxShadow: 'rgba(255, 255, 255, 0.18) 0px -1px 0px inset' }}
                type={showPassword ? 'text' : 'password'}
                name='password'
                placeholder='Create Password'
                {...register('password', {
                  required: { value: true, message: 'Please Enter Password' },
                  onChange: handlePasswordChange,
                  value: password,
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\/-])[^ ]{8,}$/,
                    message: 'Must meet all validation criteria',
                  }
                })}
              />
              {errors.password && (<span className='text-pink-200 text-sm absolute -bottom-6 left-0 right-0'>{errors.password.message}</span>)}

              <span className='absolute right-3 top-[38px] z-[10] cursor-pointer text-2xl text-richblack-100' onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
              </span>
            </label>
            <label className='relative'>
              <p className='text-[0.875rem] leading-[1.375rem] text-richblack-5 mb-1'>Confirm Password <sup className='text-pink-200'>*</sup></p>
              <input
                className='w-full p-[12px] pr-10 bg-richblack-800 rounded-[0.5rem] text-richblack-5 outline-none'
                style={{ boxShadow: 'rgba(255, 255, 255, 0.18) 0px -1px 0px inset' }}
                type={showConfirmPassword ? 'text' : 'password'}
                name='confirmPassword'
                placeholder='Confirm Password'
                {...register('confirmPassword', {
                  required: true,
                })}
              />
              {errors.confirmPassword && (<span className='text-pink-200 text-sm absolute -bottom-6 left-0 right-0'>Please Enter Confirm Password</span>)}

              <span className='absolute right-3 top-[38px] z-[10] cursor-pointer text-2xl text-richblack-100' onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                {showConfirmPassword ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
              </span>
            </label>
          </div>
          <PasswordValidator password={password} />
          <button
            type='submit'
            className='bg-yellow-50 py-[8px] px-[12px] rounded-[8px] transition-all duration-200 hover:scale-95 mt-6 font-medium text-richblack-900'
          >
            Create Account
          </button>
        </form>
      </div>
    </div>
  )
}

export default SignUpForm;